/**
 * Peak extraction for short audio clips drawn as waveforms on the solution
 * pages. A PCM WAV is read directly so no AudioContext is created just to
 * draw; anything else is decoded through the Web Audio API.
 */
export interface DecodedPeaks {
  peaks: number[][];
  duration: number;
}

const BUCKET_COUNT = 1000;

function readTag(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
}

function makeSampleReader(
  view: DataView,
  audioFormat: number,
  bitsPerSample: number,
): ((offset: number) => number) | null {
  if (audioFormat === 1) {
    if (bitsPerSample === 16) {
      return (offset) => view.getInt16(offset, true) / 0x8000;
    }
    if (bitsPerSample === 8) {
      return (offset) => (view.getUint8(offset) - 128) / 128;
    }
    if (bitsPerSample === 24) {
      return (offset) => {
        const value =
          view.getUint8(offset) |
          (view.getUint8(offset + 1) << 8) |
          (view.getUint8(offset + 2) << 16);
        const signed = value & 0x800000 ? value | ~0xffffff : value;
        return signed / 0x800000;
      };
    }
    if (bitsPerSample === 32) {
      return (offset) => view.getInt32(offset, true) / 0x80000000;
    }
  }
  if (audioFormat === 3) {
    if (bitsPerSample === 32) {
      return (offset) => view.getFloat32(offset, true);
    }
    if (bitsPerSample === 64) {
      return (offset) => view.getFloat64(offset, true);
    }
  }
  return null;
}

function parseWavPeaks(arrayBuffer: ArrayBuffer): DecodedPeaks | null {
  if (arrayBuffer.byteLength < 44) {
    return null;
  }
  const view = new DataView(arrayBuffer);
  if (readTag(view, 0) !== 'RIFF' || readTag(view, 8) !== 'WAVE') {
    return null;
  }

  let format: {
    audioFormat: number;
    channels: number;
    sampleRate: number;
    bitsPerSample: number;
  } | null = null;
  let dataOffset = -1;
  let dataLength = 0;

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const id = readTag(view, offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      format = {
        audioFormat: view.getUint16(body, true),
        channels: view.getUint16(body + 2, true),
        sampleRate: view.getUint32(body + 4, true),
        bitsPerSample: view.getUint16(body + 14, true),
      };
    } else if (id === 'data') {
      dataOffset = body;
      dataLength = size;
    }
    offset = body + size + (size % 2);
  }

  if (!format || dataOffset < 0 || format.sampleRate <= 0) {
    return null;
  }

  const readSample = makeSampleReader(
    view,
    format.audioFormat,
    format.bitsPerSample,
  );
  if (!readSample) {
    return null;
  }

  const frameSize = (format.bitsPerSample / 8) * format.channels;
  if (frameSize <= 0) {
    return null;
  }
  const safeLength = Math.min(dataLength, view.byteLength - dataOffset);
  const frameCount = Math.floor(safeLength / frameSize);
  if (frameCount <= 0) {
    return null;
  }

  const buckets = Math.min(BUCKET_COUNT, frameCount);
  const blockSize = Math.max(1, Math.floor(frameCount / buckets));
  const peaks: number[] = [];
  for (let bucket = 0; bucket < buckets; bucket += 1) {
    const startFrame = bucket * blockSize;
    let peak = 0;
    for (let index = 0; index < blockSize; index += 1) {
      const value = Math.abs(
        readSample(dataOffset + (startFrame + index) * frameSize),
      );
      if (value > peak) {
        peak = value;
      }
    }
    peaks.push(peak);
  }

  return { peaks: [peaks], duration: frameCount / format.sampleRate };
}

async function decodePeaksWithAudioContext(
  arrayBuffer: ArrayBuffer,
): Promise<DecodedPeaks> {
  const audioContext = new AudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const channel = audioBuffer.getChannelData(0);
    const buckets = Math.min(BUCKET_COUNT, channel.length);
    const blockSize = Math.max(1, Math.floor(channel.length / buckets));
    const peaks: number[] = [];
    for (let bucket = 0; bucket < buckets; bucket += 1) {
      const start = bucket * blockSize;
      let peak = 0;
      for (let index = 0; index < blockSize; index += 1) {
        const value = Math.abs(channel[start + index] ?? 0);
        if (value > peak) {
          peak = value;
        }
      }
      peaks.push(peak);
    }
    return { peaks: [peaks], duration: audioBuffer.duration };
  } finally {
    audioContext.close();
  }
}

const peaksCache = new Map<string, Promise<DecodedPeaks>>();

export function loadPeaks(url: string): Promise<DecodedPeaks> {
  const cached = peaksCache.get(url);
  if (cached) {
    return cached;
  }

  const request = (async (): Promise<DecodedPeaks> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio (${response.status})`);
    }
    const arrayBuffer = await response.arrayBuffer();
    return (
      parseWavPeaks(arrayBuffer) ?? decodePeaksWithAudioContext(arrayBuffer)
    );
  })();

  peaksCache.set(url, request);
  request.catch(() => peaksCache.delete(url));
  return request;
}
